import { useSearchParams } from 'react-router';

import { useFeature } from '@modules/root';

/** The CMS flag that puts the product gallery on the bounded rail. */
const BOUNDED_RAIL_FEATURE = 'gallery_bounded_rail';

/**
 * Which resting shape the desktop gallery row takes.
 *
 * The shipped row loops: every snap is centred, and the row is laid down as
 * many times as embla needs to actually wrap (see `loopRepeats`), so there is
 * always an image either side of the one in the middle.
 *
 * The bounded row is the alternative under trial. It is finite, and its first
 * and last slides come to rest against the page gutter (`trimSnaps`) instead
 * of centring — our slides run from 477px to 1046px, and centring a narrow one
 * at the end of the row leaves half a viewport of empty track beside it.
 * Nothing wraps, so a push past the last slide runs out into the document,
 * which is the bounce `HardEdge` hands back to the page.
 *
 * Off unless the flag says otherwise, so the row keeps looping until the trial
 * is turned on. `?galleryRail=bounded` forces it in dev.
 *
 * Read by the gallery and by the next product's grown row alike: the page turn
 * between them is only invisible while both lay the row out the same way.
 */
export const useBoundedRail = (): boolean => {
    const flagged = useFeature(BOUNDED_RAIL_FEATURE);
    const [searchParams] = useSearchParams();
    return (
        flagged ||
        (import.meta.env.DEV && searchParams.get('galleryRail') === 'bounded')
    );
};
